import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Stack,
  TextField
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import { useTheme } from "@emotion/react";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import CategoryIcon from "@mui/icons-material/Category";
import BarChartIcon from "@mui/icons-material/BarChart";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import ModernDatePicker from "../ModernDatePicker";

export const ENTITIES = [
  { value: 'patients', label: 'Patients' },
  { value: 'forms', label: 'Forms' },
  { value: 'form_answers', label: 'Form Answers' },
  { value: 'form_invitations', label: 'Form Invitations' },
  { value: 'files', label: 'Files' },
  { value: 'scoring_formulas', label: 'Scoring Formulas' }
];

const TIME_FILTERS = [
  { value: 'last_week', label: 'Last 1 Week' },
  { value: 'last_2_weeks', label: 'Last 2 Weeks' },
  { value: 'last_month', label: 'Last 1 Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'last_year', label: 'Last 1 Year' },
  { value: 'this_month', label: 'This Month' },
  { value: 'this_year', label: 'This Year' },
  { value: 'custom', label: 'Custom Date Range' }
];

const GROUP_BY_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' }
];

/**
 * Time period'dan daha büyük group by seçilemez.
 * Süreye göre sadece anlamlı gruplama seçeneklerini döndürür.
 * @param {string} timeFilter - last_week, last_2_weeks, last_month, vb.
 * @param {string} [customStartDate] - Custom range için YYYY-MM-DD
 * @param {string} [customEndDate] - Custom range için YYYY-MM-DD
 * @returns {Array<{value: string, label: string}>}
 */
function getGroupByOptionsForTimePeriod(timeFilter, customStartDate, customEndDate) {
  let approxDays = 0;
  if (timeFilter === 'custom' && customStartDate && customEndDate) {
    const start = new Date(customStartDate);
    const end = new Date(customEndDate);
    approxDays = Math.max(0, Math.ceil((end - start) / (24 * 60 * 60 * 1000)) + 1);
  } else {
    const dayCounts = {
      last_week: 7,
      last_2_weeks: 14,
      last_month: 31,
      last_3_months: 92,
      last_6_months: 183,
      last_year: 365,
      this_month: 31,
      this_year: 365
    };
    approxDays = dayCounts[timeFilter] ?? 31;
  }
  const allowed = ['none', 'day'];
  if (approxDays >= 7) allowed.push('week');
  if (approxDays >= 28) allowed.push('month');
  if (approxDays >= 90) allowed.push('quarter');
  if (approxDays >= 365) allowed.push('year');
  return GROUP_BY_OPTIONS.filter((opt) => allowed.includes(opt.value));
}

// Dashboard'da tüm entity'ler için sadece Total Count (detaylar farklı sayfalarda)
const DEFAULT_GET_METRICS = () => [{ value: 'count', label: 'Total Count' }];

export default function StatsFilters({
  entity,
  metric,
  timeFilter,
  groupBy,
  onEntityChange,
  onMetricChange,
  onTimeFilterChange,
  onGroupByChange,
  onRefresh,
  customStartDate,
  customEndDate,
  onCustomStartDateChange,
  onCustomEndDateChange,
  entities: entitiesProp,
  getMetricsForEntity: getMetricsForEntityProp,
  dashboardMode = false
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('stats');
  const entities = entitiesProp ?? ENTITIES;
  const entityValue = Array.isArray(entity) ? entity : (entity ? [entity] : []);
  const singleEntity = entityValue.length === 1 ? entityValue[0] : entityValue[0] || 'patients';
  const getMetricsForEntity = getMetricsForEntityProp ?? DEFAULT_GET_METRICS;
  const metrics = getMetricsForEntity(singleEntity);
  const isCustomDateRange = timeFilter === 'custom';
  const hideTimeAndGroupBy = !dashboardMode && singleEntity === 'files' && metric === 'forms_by_file';

  const groupByOptionsAllowed = useMemo(
    () => getGroupByOptionsForTimePeriod(timeFilter, customStartDate, customEndDate),
    [timeFilter, customStartDate, customEndDate]
  );

  useEffect(() => {
    const allowedValues = groupByOptionsAllowed.map((o) => o.value);
    if (groupBy && !allowedValues.includes(groupBy)) {
      const fallback = allowedValues.includes('day') ? 'day' : allowedValues[0];
      onGroupByChange(fallback);
    }
  // groupByOptionsAllowed / groupBy değişince geçersiz seçimi düzelt; onGroupByChange bağımlılığa eklenmez (inline ref ile döngü riski)
  }, [groupByOptionsAllowed, groupBy]);

  const selectStyles = {
    color: colors.grey[100],
    backgroundColor: `${colors.primary[800]}80`,
    borderRadius: '12px',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: `${colors.grey[700]}60`,
      borderWidth: '1.5px'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.blueAccent[500],
      borderWidth: '2px'
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.greenAccent[500],
      borderWidth: '2px'
    },
    '& .MuiSvgIcon-root': {
      color: colors.grey[300]
    },
    '& .MuiSelect-select': {
      padding: '10px 14px',
      fontWeight: 500
    },
    transition: 'all 0.3s ease'
  };

  const labelStyles = {
    color: colors.grey[300],
    fontWeight: 600,
    fontSize: '0.875rem'
  };

  // Modern dropdown menu props
  const menuProps = {
    PaperProps: {
      sx: {
        mt: 1.5,
        borderRadius: '16px',
        background: `linear-gradient(135deg, ${colors.primary[800]} 0%, ${colors.primary[700]} 100%)`,
        border: `1px solid ${colors.grey[700]}60`,
        boxShadow: `0 12px 48px rgba(0, 0, 0, 0.4)`,
        backdropFilter: 'blur(20px)',
        overflow: 'hidden',
        '& .MuiMenuItem-root': {
          borderRadius: '8px',
          mx: 1.5,
          my: 0.5,
          px: 2.5,
          py: 1.5,
          minHeight: '44px',
          fontSize: '0.9rem',
          fontWeight: 500,
          color: colors.grey[200],
          position: 'relative',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: `${colors.blueAccent[700]}40`,
            transform: 'translateX(6px)',
            boxShadow: `0 4px 12px ${colors.blueAccent[500]}30`,
            color: colors.grey[100]
          },
          '&.Mui-selected': {
            backgroundColor: `${colors.greenAccent[700]}40`,
            color: colors.greenAccent[300],
            fontWeight: 600,
            '&:hover': {
              backgroundColor: `${colors.greenAccent[600]}50`,
              transform: 'translateX(6px)',
              color: colors.greenAccent[200]
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: '4px',
              height: '70%',
              backgroundColor: colors.greenAccent[500],
              borderRadius: '0 4px 4px 0',
              boxShadow: `0 0 8px ${colors.greenAccent[500]}60`
            },
            '&::after': {
              content: '"✓"',
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: colors.greenAccent[400],
              fontSize: '1.1rem',
              fontWeight: 700
            }
          }
        }
      }
    },
    MenuListProps: {
      sx: {
        py: 1.5,
        '&::-webkit-scrollbar': {
          width: '8px'
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent'
        },
        '&::-webkit-scrollbar-thumb': {
          background: `${colors.grey[700]}60`,
          borderRadius: '10px',
          '&:hover': {
            background: `${colors.grey[600]}80`
          }
        }
      }
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left'
    },
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left'
    },
    transitionDuration: 200
  };

  return (
    <Card
      sx={{
        mb: 3,
        background: `linear-gradient(135deg, ${colors.primary[700]} 0%, ${colors.primary[600]} 100%)`,
        borderRadius: '20px',
        border: `1px solid ${colors.grey[800]}40`,
        boxShadow: `0 8px 32px rgba(0, 0, 0, 0.3)`,
        backdropFilter: 'blur(10px)',
        overflow: 'visible'
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[600]} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 4px 12px ${colors.blueAccent[500]}40`
              }}
            >
              <FilterListIcon sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: colors.grey[100],
                letterSpacing: '0.5px'
              }}
            >
              {t('filterAndConfigure')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {dashboardMode && (
              <Button
                variant="contained"
                onClick={onRefresh}
                startIcon={<FilterListIcon />}
                sx={{
                  background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
                  color: 'white',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '12px',
                  px: 2,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.greenAccent[600]} 100%)`,
                    boxShadow: `0 4px 16px ${colors.greenAccent[500]}50`
                  }
                }}
              >
                {t('filterAndConfigure').split(' ')[0] || 'Filtrele'}
              </Button>
            )}
            <Tooltip title={t('refreshData')}>
              <IconButton
                onClick={onRefresh}
                sx={{
                  backgroundColor: `${colors.greenAccent[700]}30`,
                  color: colors.greenAccent[300],
                  '&:hover': {
                    backgroundColor: `${colors.greenAccent[600]}50`,
                    transform: 'rotate(180deg)',
                    transition: 'all 0.5s ease'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Divider sx={{ mb: 3, borderColor: `${colors.grey[700]}40` }} />

        {/* Filter Controls */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          {/* Entity Selector (multi when dashboardMode) */}
          <FormControl size="medium" sx={{ minWidth: { xs: '100%', md: 200 }, flex: 1 }}>
            <InputLabel sx={labelStyles}>{t('entity')}</InputLabel>
            <Select
              multiple={dashboardMode}
              value={dashboardMode ? entityValue : (entityValue[0] || '')}
              label={t('entity')}
              onChange={(e) => {
                const v = e.target.value;
                if (dashboardMode) {
                  const newVal = typeof v === 'string' ? (v ? [v] : []) : (v || []);
                  if (newVal.length === 0) return;
                  onEntityChange(newVal);
                } else {
                  onEntityChange(typeof v === 'string' ? v : (v && v[0]) || '');
                }
              }}
              sx={selectStyles}
              MenuProps={menuProps}
              renderValue={dashboardMode ? (selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(Array.isArray(selected) ? selected : []).map((val) => (
                    <Chip
                      key={val}
                      size="small"
                      label={t(`entities.${val}`) || entities.find((e) => e.value === val)?.label || val}
                      sx={{
                        backgroundColor: colors.blueAccent[700],
                        color: 'white',
                        fontWeight: 600,
                        height: 24
                      }}
                    />
                  ))}
                </Box>
              ) : undefined}
            >
              {entities.map((ent) => (
                <MenuItem
                  key={ent.value}
                  value={ent.value}
                  selected={entityValue.includes(ent.value)}
                >
                  {t(`entities.${ent.value}`) || ent.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Metric Selector (hidden in dashboard mode – always count) */}
          {!dashboardMode && (
            <FormControl size="medium" sx={{ minWidth: { xs: '100%', md: 220 }, flex: 1 }}>
              <InputLabel sx={labelStyles}>{t('metric')}</InputLabel>
              <Select
                value={metric}
                label={t('metric')}
                onChange={(e) => onMetricChange(e.target.value)}
                sx={selectStyles}
                MenuProps={menuProps}
              >
                {metrics.map((m) => (
                  <MenuItem
                    key={m.value}
                    value={m.value}
                    selected={metric === m.value}
                  >
                    {t(`metrics.${m.value}`) || m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Time Filter - Forms by File'da anlamı yok, gizle */}
          {!hideTimeAndGroupBy && (
            <FormControl size="medium" sx={{ minWidth: { xs: '100%', md: 180 }, flex: 1 }}>
              <InputLabel sx={labelStyles}>Time Period</InputLabel>
              <Select
                value={timeFilter}
                label="Time Period"
                onChange={(e) => onTimeFilterChange(e.target.value)}
                sx={selectStyles}
                MenuProps={menuProps}
              >
                {TIME_FILTERS.map((filter) => (
                  <MenuItem
                    key={filter.value}
                    value={filter.value}
                    selected={timeFilter === filter.value}
                  >
                    {t(`timeFilters.${filter.value}`) || filter.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Group By - Forms by File'da anlamı yok, gizle */}
          {!hideTimeAndGroupBy && (
            <FormControl size="medium" sx={{ minWidth: { xs: '100%', md: 140 }, flex: 1 }}>
              <InputLabel sx={labelStyles}>Group By</InputLabel>
              <Select
                value={groupBy}
                label="Group By"
                onChange={(e) => onGroupByChange(e.target.value)}
                sx={selectStyles}
                MenuProps={menuProps}
              >
                {groupByOptionsAllowed.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    selected={groupBy === option.value}
                  >
                    {t(`groupByOptions.${option.value}`) || option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>

        {/* Custom Date Range Inputs - Forms by File'da gizle */}
        {!hideTimeAndGroupBy && isCustomDateRange && (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'center',
              mb: 2,
              pt: 2,
              borderTop: `1px solid ${colors.grey[700]}30`
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: colors.grey[400],
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                fontSize: '0.7rem',
                width: '100%'
              }}
            >
              {t('customDateRange')}:
            </Typography>
            <Box sx={{ minWidth: { xs: '100%', md: 180 }, flex: 1 }}>
              <ModernDatePicker
                value={customStartDate || ''}
                onChange={onCustomStartDateChange}
                maxDate={customEndDate ? customEndDate : new Date().toISOString().split('T')[0]} // End date varsa onu, yoksa bugünü max yap
                placeholder={t('selectStartDate')}
                withPortal={false}
                portalId="stats-datepicker-portal"
                popperPlacement="bottom-start"
                popperClassName="stats-datepicker-popper-high-z"
                sx={{
                  '& .MuiFilledInput-root': {
                    backgroundColor: `${colors.primary[800]}80`,
                    borderRadius: '12px',
                    border: `1.5px solid ${colors.grey[700]}60`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: colors.primary[700],
                      borderColor: colors.blueAccent[500],
                    },
                    '&.Mui-focused': {
                      backgroundColor: colors.primary[700],
                      borderColor: colors.greenAccent[500],
                      boxShadow: `0 0 0 3px ${colors.greenAccent[500]}33`,
                    },
                  },
                  '& .MuiFilledInput-input': {
                    color: colors.grey[100],
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    padding: '10px 14px',
                  },
                }}
              />
            </Box>
            <Box sx={{ minWidth: { xs: '100%', md: 180 }, flex: 1 }}>
              <ModernDatePicker
                value={customEndDate || ''}
                onChange={onCustomEndDateChange}
                minDate={customStartDate || undefined}
                maxDate={new Date().toISOString().split('T')[0]} // Bugünden sonraki tarihleri engelle
                placeholder={t('selectEndDate')}
                withPortal={false}
                portalId="stats-datepicker-portal"
                popperPlacement="bottom-start"
                popperClassName="stats-datepicker-popper-high-z"
                sx={{
                  '& .MuiFilledInput-root': {
                    backgroundColor: `${colors.primary[800]}80`,
                    borderRadius: '12px',
                    border: `1.5px solid ${colors.grey[700]}60`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: colors.primary[700],
                      borderColor: colors.blueAccent[500],
                    },
                    '&.Mui-focused': {
                      backgroundColor: colors.primary[700],
                      borderColor: colors.greenAccent[500],
                      boxShadow: `0 0 0 3px ${colors.greenAccent[500]}33`,
                    },
                  },
                  '& .MuiFilledInput-input': {
                    color: colors.grey[100],
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    padding: '10px 14px',
                  },
                }}
              />
            </Box>
          </Box>
        )}

        {/* Active Filters Display */}
        <Box
          sx={{
            pt: 2,
            borderTop: `1px solid ${colors.grey[700]}30`
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              flexWrap: 'wrap',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: colors.grey[400],
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                fontSize: '0.7rem'
              }}
            >
              {t('activeFilters') || 'Active Filters'}:
            </Typography>
          {dashboardMode ? (
            entityValue.map((ent) => (
              <Chip
                key={ent}
                icon={<CategoryIcon sx={{ fontSize: 16 }} />}
                label={t(`entities.${ent}`) || entities.find((e) => e.value === ent)?.label || ent}
                size="small"
                sx={{
                  backgroundColor: colors.blueAccent[700],
                  color: 'white',
                  fontWeight: 600,
                  height: 28,
                  '& .MuiChip-icon': { color: 'white' },
                  '&:hover': {
                    backgroundColor: colors.blueAccent[600],
                    transform: 'scale(1.05)',
                    transition: 'all 0.2s ease'
                  }
                }}
              />
            ))
          ) : (
            <Chip
              icon={<CategoryIcon sx={{ fontSize: 16 }} />}
              label={entities.find((e) => e.value === singleEntity)?.label || singleEntity}
              size="small"
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: 'white',
                fontWeight: 600,
                height: 28,
                '& .MuiChip-icon': { color: 'white' },
                '&:hover': {
                  backgroundColor: colors.blueAccent[600],
                  transform: 'scale(1.05)',
                  transition: 'all 0.2s ease'
                }
              }}
            />
          )}
          {(!dashboardMode || true) && (
            <Chip
              icon={<BarChartIcon sx={{ fontSize: 16 }} />}
              label={dashboardMode ? (t('metrics.count') || t('totalCount') || 'Toplam Sayı') : (t(`metrics.${metric}`) || metrics.find((m) => m.value === metric)?.label)}
              size="small"
              sx={{
                backgroundColor: colors.greenAccent[700],
                color: 'white',
                fontWeight: 600,
                height: 28,
                '& .MuiChip-icon': { color: 'white' },
                '&:hover': {
                  backgroundColor: colors.greenAccent[600],
                  transform: 'scale(1.05)',
                  transition: 'all 0.2s ease'
                }
              }}
            />
          )}
          {!hideTimeAndGroupBy && (
            <Chip
              icon={<CalendarTodayIcon sx={{ fontSize: 16 }} />}
              label={
                isCustomDateRange && customStartDate && customEndDate
                  ? `${customStartDate} to ${customEndDate}`
                  : (t(`timeFilters.${timeFilter}`) || TIME_FILTERS.find(f => f.value === timeFilter)?.label)
              }
              size="small"
              sx={{
                backgroundColor: colors.primary[500],
                color: 'white',
                fontWeight: 600,
                height: 28,
                '& .MuiChip-icon': {
                  color: 'white'
                },
                '&:hover': {
                  backgroundColor: colors.primary[400],
                  transform: 'scale(1.05)',
                  transition: 'all 0.2s ease'
                }
              }}
            />
          )}
          {!hideTimeAndGroupBy && groupBy !== 'none' && (
            <Chip
              icon={<ViewWeekIcon sx={{ fontSize: 16 }} />}
              label={`${t('groupLabel')}: ${t(`groupByOptions.${groupBy}`) || GROUP_BY_OPTIONS.find(g => g.value === groupBy)?.label}`}
              size="small"
              sx={{
                backgroundColor: colors.grey[700],
                color: 'white',
                fontWeight: 600,
                height: 28,
                '& .MuiChip-icon': {
                  color: 'white'
                },
                '&:hover': {
                  backgroundColor: colors.grey[600],
                  transform: 'scale(1.05)',
                  transition: 'all 0.2s ease'
                }
              }}
            />
          )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
